import { coachZone, dayKey, dayKeyOf, lastTrainedDayKey, shiftDayKey, trainedDayKey } from "./days";
import { embeddedWorkout, isOptionalTask, isTaskAssignment, type AssignmentEmbed, type RosterRow } from "./roster";
import { mondayKey, weekCells, type WeekCell } from "./weekLine";

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE ROSTER'S DERIVATION, ONCE — read by the Clients page AND the This week board (W72 step 1).
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * 🔴 IT WAS INLINE IN `Roster.tsx` UNTIL THE BOARD NEEDED IT, AND A SECOND COPY IS THE BUG THIS REPO
 * IS NAMED AFTER. Both screens draw one person's week; if each derived it, the first rule change
 * would land in one and the board would say "missed Wednesday" about a day the roster calls done.
 * So the four raw reads (`coach_clients`, `profiles`, `coach_assignments`, `set_logs`) go in and every
 * number both screens show comes out, from here and nowhere else. The logic below is MOVED, not
 * rewritten — its reasons stay in `Roster.tsx`'s history and in the comments carried with it.
 *
 * ⚠️ `now` IS A PARAMETER, read once by the caller: every row is judged against the same "today", so
 * two rows can never disagree about what day it is across a midnight boundary.
 */

export type RawLink = {
  id: string;
  client_id: string | null;
  status: string;
  invited_first_name: string | null;
  invited_last_name: string | null;
  invited_email: string | null;
  created_at: string | null;
  /** N2g — when coaching ended (or a link was withdrawn); null for a client's own refusal. Optional: hand-built fixtures. */
  ended_at?: string | null;
  /** The pending invitation's token — the coach reads their own row's (`invite.ts` does on create). */
  invite_token?: string | null;
  invite_token_expires_at?: string | null;
};
export type RawProfile = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  created_at: string | null;
  main_goals?: string[] | null;
  training_days_per_week?: number | null;
  /** the photo the athlete set on the phone (public `avatars` bucket), or null — step 7's avatar draws initials then */
  avatar_url?: string | null;
};
export type RawAssignment = {
  client_id: string | null;
  scheduled_date: string | null;
  workout_id?: string | null;
  root_workout_id?: string | null;
  coach_workouts?: AssignmentEmbed;
};
/** A logged set and — W112 — its parent session's `scheduled_date` (absent/null when the session has none). */
export type RawSet = { user_id: string | null; completed_at: string | null; scheduled_date?: string | null };

/**
 * W112 — THE ONE SELECT FOR A LOGGED SET AND ITS SESSION'S SCHEDULED DAY (the roster's read and the client page's read):
 * `set_logs` → `workout_session_exercises` → `workout_sessions`, both many-to-one, so ONE request as before.
 */
export const SET_DAY_SELECT = "user_id, completed_at, workout_session_exercises(workout_sessions(scheduled_date))";

type Embedded<T> = T | readonly T[] | null | undefined;
const one = <T,>(x: Embedded<T>): T | null => (Array.isArray(x) ? ((x as readonly T[])[0] ?? null) : ((x as T | null | undefined) ?? null));

/** A `SET_DAY_SELECT` row as a `RawSet`. ⚠️ A PostgREST embed arrives as OBJECT or ARRAY by inferred relationship (the
 *  codex) — both are read, at both levels. */
export function rawSetOf(row: {
  user_id: string | null; completed_at: string | null;
  workout_session_exercises?: Embedded<{ workout_sessions?: Embedded<{ scheduled_date: string | null }> }>;
}): RawSet {
  const wse = one(row.workout_session_exercises);
  const ws = wse === null ? null : one(wse.workout_sessions);
  return { user_id: row.user_id, completed_at: row.completed_at, scheduled_date: ws?.scheduled_date ?? null };
}

/**
 * 🔴 THE ROSTER'S "TRAINED" SIGNAL, AS ONE FUNCTION: a set counts when it carries a `completed_at` — a LOGGED set,
 * never `workout_sessions.completed_at` (the Finish button, empty for most real training; `roster.ts`'s header). The
 * roster's last-trained day and the review queue's "finished" both read THIS, so the two can never disagree about
 * whether somebody did something (W82 step 2 moved it out of `deriveRoster`'s loop, unchanged).
 */
export function loggedAt(set: { completed_at: string | null }): string | null {
  return set.completed_at;
}

/**
 * When a session record FINISHED, by the roster's rule extended to a record (W82 §1): it holds ≥ 1 logged set, and
 * its finish is `completed_at` when the athlete pressed Finish, else its last logged set's time. Null = not finished
 * (nothing logged), however its status reads. ISO strings compare as instants only in one spelling, so the latest
 * is taken by parsed time.
 */
export function sessionFinishedAt(completedAt: string | null, sets: readonly { completed_at: string | null }[]): string | null {
  const stamps = sets.map(loggedAt).filter((s): s is string => s !== null);
  if (stamps.length === 0) return null;
  if (completedAt !== null) return completedAt;
  return stamps.reduce((a, b) => (Date.parse(b) > Date.parse(a) ? b : a));
}

export type Invited = { firstName: string | null; lastName: string | null; email: string | null };

/** What a row shows beside the name — the person's own setup answers, never a guess. */
export type RosterProfile = {
  /** `profiles.main_goals[0]` — the slug; the label is `mainGoals.ts`'s. */
  mainGoal: string | null;
  /** `profiles.training_days_per_week`, or null when there is no account behind the row. */
  daysPerWeek: number | null;
  /** `profiles.avatar_url` — the avatar's photo when there is one (pro web step 7). */
  photoUrl: string | null;
};

export type RosterData = {
  rows: RosterRow[];
  invited: Record<string, Invited>;
  /** This week's seven cells per link, from `weekCells` — the phone's and the client page's rule. */
  weeks: Record<string, WeekCell[]>;
  /** Whether anything is assigned in NEXT week's seven days, per link. */
  nextWeek: Record<string, boolean>;
  /**
   * The FIRST training day assigned AFTER this week's Sunday, per link, or null when next week onwards
   * holds nothing. ONE fact answers both the roster's "Next assigned" and the board's "nothing after
   * Sunday", so the two cannot disagree about whether a client's next week is written.
   */
  firstAfterWeekISO: Record<string, string | null>;
  /**
   * W111 — the LAST training day assigned to the client (any date; tasks excluded, the roster's `missed` predicate), per link,
   * or null when they have none. The Dashboard's `critical` reads it: nothing assigned after a day that is near.
   */
  lastAssignedISO: Record<string, string | null>;
  profile: Record<string, RosterProfile>;
  /**
   * The training assignments per link, as the next-week reading takes them (`nextWeekPairs`). Tasks
   * are left out for the reason `Roster.tsx` always gave: a daily wrist series is not a week.
   */
  trainingByLink: Record<string, { workoutId: string; rootId: string | null; scheduledDateISO: string | null }[]>;
  /**
   * A pending row's own join link, for its `Copy link` — the path form `href({name: "join"})` builds
   * (the one a universal link can match). `expired` when the token's date has passed; null when the
   * row is active or carries no token.
   */
  inviteLink: Record<string, { token: string; expired: boolean } | null>;
};

export function deriveRoster(
  raw: { links: readonly RawLink[]; profiles: readonly RawProfile[]; assignments: readonly RawAssignment[]; sets: readonly RawSet[] },
  now: Date,
  /** The coach's zone — used ONLY for a set whose session has no scheduled day (W112). */
  zone: string = coachZone(),
): RosterData {
  const profileById = new Map(raw.profiles.map((p) => [p.id, p]));

  const assignmentCounts = new Map<string, number>();
  const assignedByClient = new Map<string, Map<string, string[]>>();
  const taskDaysByClient = new Map<string, Map<string, string[]>>();
  const trainingIdsByClient = new Map<string, { workoutId: string; rootId: string | null; scheduledDateISO: string | null }[]>();
  for (const a of raw.assignments) {
    if (a.client_id === null) continue;
    // Counted BEFORE the kind is read: `attention`'s "No training assigned" asks whether this coach
    // has given them ANYTHING, and a daily task is something.
    assignmentCounts.set(a.client_id, (assignmentCounts.get(a.client_id) ?? 0) + 1);
    const w = a.coach_workouts;
    const { name } = embeddedWorkout(w);
    const isTask = isTaskAssignment(w);
    if (!isTask && a.workout_id != null) {
      const list = trainingIdsByClient.get(a.client_id) ?? [];
      list.push({ workoutId: a.workout_id, rootId: a.root_workout_id ?? null, scheduledDateISO: a.scheduled_date });
      trainingIdsByClient.set(a.client_id, list);
    }
    const day = a.scheduled_date === null ? null : dayKey(a.scheduled_date);
    if (day === null) continue;
    // 🔴 AN OPTIONAL TASK IS OUTSIDE EVERY COUNT (his 2026-10-07: offered, never owed) — not in
    // Tasks 7d / 30d and not among the week cell's task titles. It was counted above as "something
    // assigned", which it is; it is just never something owed.
    if (isTask && isOptionalTask(w)) continue;
    const target = isTask ? taskDaysByClient : assignedByClient;
    const byDay = target.get(a.client_id) ?? new Map<string, string[]>();
    byDay.set(day, [...(byDay.get(day) ?? []), name ?? (isTask ? "Task" : "Workout")]);
    target.set(a.client_id, byDay);
  }

  // W112 — a set counts on its session's SCHEDULED day (`trainedDayKey`); only an unscheduled one reads its instant.
  const trainedByClient = new Map<string, Set<string>>();
  for (const s of raw.sets) {
    const at = loggedAt(s);
    if (s.user_id === null || at === null) continue;
    const day = trainedDayKey(s.scheduled_date, at, zone);
    if (day === null) continue;
    const set = trainedByClient.get(s.user_id) ?? new Set<string>();
    set.add(day);
    trainedByClient.set(s.user_id, set);
  }

  const todayKey = dayKeyOf(now);
  const lastTrained = new Map<string, string>();
  for (const [id, days] of trainedByClient) {
    const last = lastTrainedDayKey(days, todayKey);
    if (last !== null) lastTrained.set(id, last);
  }
  const nextMonday = mondayKey(now, 1);
  const thisSunday = shiftDayKey(new Date(`${mondayKey(now, 0)}T00:00:00Z`), 6);
  const nextWeekDays = new Set(
    Array.from({ length: 7 }, (_, i) => shiftDayKey(new Date(`${nextMonday}T00:00:00Z`), i)),
  );

  const out: RosterData = {
    rows: [], invited: {}, weeks: {}, nextWeek: {}, firstAfterWeekISO: {}, lastAssignedISO: {}, profile: {}, trainingByLink: {}, inviteLink: {},
  };
  for (const l of raw.links) {
    const cid = l.client_id;
    const p = cid !== null ? profileById.get(cid) : undefined;
    const assigned = cid === null ? undefined : assignedByClient.get(cid);
    const trained = cid === null ? new Set<string>() : (trainedByClient.get(cid) ?? new Set<string>());
    const taskDays = cid === null ? undefined : taskDaysByClient.get(cid);
    const assignedKeys = [...(assigned?.keys() ?? [])].sort();

    out.invited[l.id] = { firstName: l.invited_first_name, lastName: l.invited_last_name, email: l.invited_email };
    out.nextWeek[l.id] = assignedKeys.some((d) => nextWeekDays.has(d));
    out.firstAfterWeekISO[l.id] = assignedKeys.find((d) => d > thisSunday) ?? null;
    out.lastAssignedISO[l.id] = assignedKeys[assignedKeys.length - 1] ?? null;
    out.profile[l.id] = {
      mainGoal: p?.main_goals?.[0] ?? null,
      daysPerWeek: p?.training_days_per_week ?? null,
      photoUrl: p?.avatar_url ?? null,
    };
    out.trainingByLink[l.id] = cid === null ? [] : (trainingIdsByClient.get(cid) ?? []);
    out.inviteLink[l.id] = l.status !== "active" && l.invite_token != null && l.invite_token !== ""
      ? { token: l.invite_token, expired: l.invite_token_expires_at != null && l.invite_token_expires_at < now.toISOString() }
      : null;
    if (cid !== null) {
      // The SAME two maps the client page passes — the rule lives in `weekCells`.
      out.weeks[l.id] = weekCells(now, assigned ?? new Map(), trained, 0, taskDays ?? new Map());
    }
    out.rows.push({
      linkId: l.id,
      clientId: cid,
      status: l.status,
      firstName: p?.first_name ?? null,
      lastName: p?.last_name ?? null,
      assignmentCount: cid !== null ? (assignmentCounts.get(cid) ?? 0) : 0,
      last7Tasks: trainingCount(taskDays?.keys() ?? [], trained, now, 7),
      last30Tasks: trainingCount(taskDays?.keys() ?? [], trained, now, 30),
      // `dayKey`, never the raw instant: the cell draws a DATE and the sort compares `YYYY-MM-DD`.
      signedUpISO: p?.created_at != null ? dayKey(p.created_at) : null,
      invitedISO: l.created_at != null ? dayKey(l.created_at) : null,
      endedISO: l.ended_at != null ? dayKey(l.ended_at) : null,
      last7: trainingCount(assignedKeys, trained, now, 7),
      // The SOONEST assigned day that is today or later — "when does their next session fall".
      nextAssignedISO: assignedKeys.find((d) => d >= todayKey) ?? null,
      last30: trainingCount(assignedKeys, trained, now, 30),
      lastTrainedISO: cid !== null ? (lastTrained.get(cid) ?? null) : null,
    });
  }
  return out;
}

/**
 * A training counter over a window, as COMPLETED / ASSIGNED — never a bare number.
 *
 * 🔴 EVERFIT PRINTS `0%` AND `--` IN THESE CELLS AND BOTH ARE UNREADABLE (measured on his live
 * roster tonight: `Last 7d Training --`, `Last 30d Training 0%`, `Last 7d Tasks --`). A percentage
 * hides its denominator, so `0%` cannot be told apart from "assigned nothing, so nothing to
 * complete" — which is a completely different message to a coach, and the one that would make them
 * open the wrong conversation. `--` says even less.
 *
 * ⚠️ SO THE RULE THIS REPO ALREADY HAS APPLIES: every counter is completed/ASSIGNED. `3 / 4` is
 * actionable; `75%` is a grade, and `0 / 0` says the honest thing that `0%` cannot.
 *
 * ⚠️ "COMPLETED" IS A DAY ON WHICH A SET WAS LOGGED, which is `roster.ts`'s rule (moved here from `clientStatus.ts` with W72 step 1, when that file's Everfit-shaped status rail went) and not
 * `workout_sessions.completed_at` — the column that meant "pressed Finish" and told a coach his
 * client had never trained after she logged 44 sets.
 */
export function trainingCount(
  assignedDays: Iterable<string>,
  trainedDays: Set<string>,
  today: Date,
  windowDays: number,
): { completed: number; assigned: number } {
  const from = new Date(today);
  from.setUTCDate(from.getUTCDate() - (windowDays - 1));
  const fromKey = from.toISOString().slice(0, 10);
  const todayKey = today.toISOString().slice(0, 10);
  let assigned = 0;
  let completed = 0;
  for (const day of assignedDays) {
    // The window is INCLUSIVE at both ends and never looks into the future: a workout assigned
    // for Saturday is not a workout somebody failed to do on Thursday.
    if (day < fromKey || day > todayKey) continue;
    assigned += 1;
    if (trainedDays.has(day)) completed += 1;
  }
  return { completed, assigned };
}
